'use client';
import React, { useState, useEffect, useCallback } from 'react';
import ForgotPasswordForm from '../ui/forgot-password-form';
import LoginForm from '../ui/login-form';
import RegisterForm from '../ui/register-form';
import { AuthEventKeys, AuthEvents } from '../events';
import { EventBus } from '@/shared/utils';
import {
  authenticateUser,
  registerUser,
  forgotPassword,
  resetPassword,
  activateUser,
  resendRegistrationEmail,
  updatePassword,
  oidcAuthorize,
  registerGuestUser,
} from '../services/auth-service';
import ActivateResult from '../ui/activate-result';
import ForgotPasswordOk from '../ui/forgot-password-ok';
import NewPassword from '../ui/new-password';
import RegisterEmailSent from '../ui/register-email-sent';
import RegisterSection from '../ui/register-section';
import NewPasswordOk from '../ui/new-password-ok';
import { isRight, isLeft } from '@shared-commons/utils';
import { Auth } from 'firebase/auth';
import { Auth as AuthDict } from '@/shared/i18n';
import { sendGTMEvent } from 'src/shared/utils/gtm';
import UpdatePassword from '../ui/update-password';
import UpdatePasswordOk from '../ui/update-password-ok';
import { useRouter, useSearchParams } from 'next/navigation';
import OidcLoginForm from '../ui/oidc-login-form';
import CompleteRegistrationForm from '../ui/complete-registration-form';
import { getCheckoutAfterLoginObj } from '../services/checkout-after-login-obj';

export type FormType =
  | 'login'
  | 'register'
  | 'activate'
  | 'activateResult'
  | 'RegisterEmailSent'
  | 'forgotPassword'
  | 'forgotPasswordOk'
  | 'newPassword'
  | 'newPasswordOk'
  | 'updatePassword'
  | 'updatePasswordOk'
  | 'oidcLogin'
  | 'completeRegistration';

type LoginScreenProps = {
  eventBus: EventBus<AuthEvents>;
  dict: AuthDict;
  auth?: Auth;
  loginProfileError?: Error | null;
};

function LoginScreen({ eventBus, dict, auth, loginProfileError }: LoginScreenProps) {
  const [formType, setFormType] = useState<FormType>();

  const [credentials, setCredentials] = useState<{ email: string; name: string } | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();

  const prefilledEmail = searchParams.get('prefilled_email');
  const checkoutAfterLogin = searchParams.get('checkout_after_login');
  const checkoutAfterLoginObj = getCheckoutAfterLoginObj(checkoutAfterLogin);

  const handleLogin = useCallback(
    async (data: { email: string; password: string; auth: Auth }) => {
      const result = await authenticateUser(data.email, data.password, data.auth)();
      if (isRight(result) && result.value) {
        eventBus.emit(AuthEventKeys.login.success, { user: result.value, checkoutAfterLoginObj });
      } else if (isLeft(result)) {
        eventBus.emit(
          AuthEventKeys.login.error,
          new Error(result.value.message || 'Errore di login'),
        );
      }
    },
    [eventBus],
  );

  const handleOidcLogin = useCallback(
    async ({ email, password, token }: { email?: string; password?: string; token?: string }) => {
      const client_id = searchParams.get('client_id');
      const redirect_uri = searchParams.get('redirect_uri');
      const state = searchParams.get('state');

      const result = await oidcAuthorize({
        client_id,
        email,
        password,
        redirect_uri,
        state,
        token,
      })();
      if (isRight(result) && result.value.valid === true) {
        window.location.href = result.value.redirectUrl;
        console.log('Successfully authorized user');
      } else {
        const error = new Error('Credenziali non valide. Verifica email e password e riprova.');
        eventBus.emit(AuthEventKeys.oidcLogin.error, error);
      }
    },
    [eventBus],
  );

  const handleResendRegistration = useCallback(
    async (userInfo: { email: string; name: string }) => {
      const result = await resendRegistrationEmail(userInfo.email, userInfo.name)();
      if (isRight(result)) {
        console.log('Successfully resent registration email');
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore durante la registrazione');
        eventBus.emit(AuthEventKeys.register.error, error);
      }
    },
    [eventBus],
  );

  const handleRegister = useCallback(
    async (userInfo: { email: string; password: string; name: string; auth: Auth }) => {
      const result = await registerUser(
        userInfo.email,
        userInfo.password,
        userInfo.name,
        userInfo.auth,
      )();

      sendGTMEvent({
        event: 'sign_up',
        method: 'email',
      });

      if (isRight(result)) {
        setCredentials({ name: credentials?.name || '', email: userInfo.email });
        eventBus.emit(AuthEventKeys.register.success, undefined);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore durante la registrazione');
        eventBus.emit(AuthEventKeys.register.error, error);
      }
    },
    [eventBus],
  );

  const handleActivate = useCallback(
    async (params: { activationCode: string; auth: Auth }) => {
      try {
        const result = await activateUser(params.activationCode, params.auth)();
        if (isRight(result)) {
          eventBus.emit(AuthEventKeys.activate.success, undefined);
        } else if (isLeft(result)) {
          const error = result.value;
          eventBus.emit(AuthEventKeys.activate.error, error);
        }
      } catch (err) {
        console.error("Errore imprevisto durante l'attivazione:", err);
      }
    },
    [eventBus],
  );

  const handleForgotPassword = useCallback(
    async (email: string) => {
      const result = await forgotPassword(email)();
      if (isRight(result)) {
        setCredentials({ name: credentials?.name || '', email: email });
        eventBus.emit(AuthEventKeys.forgotPassword.success, undefined);
      } else if (isLeft(result)) {
        const error = new Error(
          result.value?.message || 'Errore durante il recupero della password',
        );
        eventBus.emit(AuthEventKeys.forgotPassword.error, error);
      }
    },
    [eventBus],
  );

  const handleNewPassword = useCallback(
    async (data: { password: string; confirmPassword: string; oobCode: string; auth: Auth }) => {
      if (data.password !== data.confirmPassword) {
        eventBus.emit(AuthEventKeys.newPassword.error, new Error('Le password non corrispondono'));
        return;
      }

      const result = await resetPassword(data.oobCode, data.password, data.auth)();

      if (isRight(result)) {
        eventBus.emit(AuthEventKeys.newPassword.success, undefined);
      } else if (isLeft(result)) {
        const error = result.value;
        eventBus.emit(AuthEventKeys.newPassword.error, error);
      }
    },
    [eventBus],
  );

  const handleUpdatePassword = useCallback(
    async (data: { password: string; confirmPassword: string; email: string; auth: Auth }) => {
      if (data.password !== data.confirmPassword) {
        eventBus.emit(
          AuthEventKeys.updatePassword.error,
          new Error('Le password non corrispondono'),
        );
        return;
      }

      const r = await updatePassword(data.email)();
      if (isRight(r)) {
        const result = await resetPassword(r.value.oobCode, data.password, data.auth)();

        if (isRight(result)) {
          eventBus.emit(AuthEventKeys.updatePassword.success, undefined);
        } else if (isLeft(result)) {
          const error = result.value;
          eventBus.emit(AuthEventKeys.updatePassword.error, error);
        }
      } else if (isLeft(r)) {
        const error = new Error(r.value?.message || 'Errore durante il recupero della password');
        eventBus.emit(AuthEventKeys.updatePassword.error, error);
      }
    },
    [eventBus],
  );

  const handleCompleteRegistration = useCallback(
    async (userInfo: {
      email: string;
      password: string;
      name: string;
      oobCode: string;
      auth: Auth;
    }) => {
      const result = await registerGuestUser({
        auth: userInfo.auth,
        email: userInfo.email,
        password: userInfo.password,
        name: userInfo.name,
        oobCode: userInfo.oobCode,
      })();

      sendGTMEvent({
        event: 'sign_up',
        method: 'email',
      });

      if (isRight(result)) {
        setCredentials({ name: credentials?.name || '', email: userInfo.email });
        router.push(`/auth/login?prefilled_email=${userInfo.email}`);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore durante la registrazione');
        eventBus.emit(AuthEventKeys.completeRegistration.error, error);
      }
    },
    [eventBus],
  );

  useEffect(() => {
    const handleRegisterSuccess = () => setFormType('RegisterEmailSent');
    const handleForgotPasswordSuccess = () => setFormType('forgotPasswordOk');
    const handleForgotPasswordNavigation = () => setFormType('forgotPassword');
    const handleLoginNavigation = () => setFormType('login');
    const handleRegisterNavigation = () => setFormType('register');
    const handleNewPasswordView = () => setFormType('newPassword');
    const handleNewPasswordSuccess = () => setFormType('newPasswordOk');
    const handleUpdatePasswordView = () => setFormType('updatePassword');
    const handleUpdatePasswordSuccess = () => setFormType('updatePasswordOk');
    const handleActivateSuccess = () => setFormType('activateResult');
    const handleActivateError = () => setFormType('activateResult');
    const handleOidcLoginView = () => setFormType('oidcLogin');
    const handleCompleteRegistrationNavigation = () => setFormType('completeRegistration');

    // Nuovo handler per mostrare NewPasswordOk al successo

    // Login Events
    eventBus.on(AuthEventKeys.login.view, handleLoginNavigation);
    eventBus.on(AuthEventKeys.login.do, handleLogin);

    // OIDC Login Events
    eventBus.on(AuthEventKeys.oidcLogin.view, handleOidcLoginView);
    eventBus.on(AuthEventKeys.oidcLogin.do, handleOidcLogin);

    // Register Events
    eventBus.on(AuthEventKeys.register.view, handleRegisterNavigation);
    eventBus.on(AuthEventKeys.register.do, handleRegister);
    eventBus.on(AuthEventKeys.register.success, handleRegisterSuccess);
    eventBus.on(AuthEventKeys.resendRegistration.do, handleResendRegistration);

    // Forgot Password Events
    eventBus.on(AuthEventKeys.forgotPassword.view, handleForgotPasswordNavigation);
    eventBus.on(AuthEventKeys.forgotPassword.do, handleForgotPassword);
    eventBus.on(AuthEventKeys.forgotPassword.success, handleForgotPasswordSuccess);

    // New Password Events
    eventBus.on(AuthEventKeys.newPassword.view, handleNewPasswordView);
    eventBus.on(AuthEventKeys.newPassword.do, handleNewPassword);
    eventBus.on(AuthEventKeys.newPassword.success, handleNewPasswordSuccess);

    // Activate Events
    eventBus.on(AuthEventKeys.activate.do, handleActivate);
    eventBus.on(AuthEventKeys.activate.success, handleActivateSuccess);
    eventBus.on(AuthEventKeys.activate.error, handleActivateError);

    // Update Password Events
    eventBus.on(AuthEventKeys.updatePassword.view, handleUpdatePasswordView);
    eventBus.on(AuthEventKeys.updatePassword.do, handleUpdatePassword);
    eventBus.on(AuthEventKeys.updatePassword.success, handleUpdatePasswordSuccess);

    // Complete registration events
    eventBus.on(AuthEventKeys.completeRegistration.view, handleCompleteRegistrationNavigation);
    eventBus.on(AuthEventKeys.completeRegistration.do, handleCompleteRegistration);

    return () => {
      eventBus.off(AuthEventKeys.login.view, handleLoginNavigation);
      eventBus.off(AuthEventKeys.login.do, handleLogin);

      eventBus.off(AuthEventKeys.oidcLogin.view, handleOidcLoginView);
      eventBus.off(AuthEventKeys.oidcLogin.do, handleOidcLogin);

      eventBus.off(AuthEventKeys.register.view, handleRegisterNavigation);
      eventBus.off(AuthEventKeys.register.do, handleRegister);
      eventBus.off(AuthEventKeys.register.success, handleRegisterSuccess);
      eventBus.off(AuthEventKeys.resendRegistration.do, handleResendRegistration);

      eventBus.off(AuthEventKeys.forgotPassword.success, handleForgotPasswordSuccess);
      eventBus.off(AuthEventKeys.forgotPassword.view, handleForgotPasswordNavigation);
      eventBus.off(AuthEventKeys.forgotPassword.do, handleForgotPassword);

      eventBus.off(AuthEventKeys.newPassword.view, handleNewPasswordView);
      eventBus.off(AuthEventKeys.newPassword.do, handleNewPassword);
      eventBus.off(AuthEventKeys.newPassword.success, handleNewPasswordSuccess);

      // Update Password Events
      eventBus.off(AuthEventKeys.updatePassword.view, handleUpdatePasswordView);
      eventBus.off(AuthEventKeys.updatePassword.do, handleUpdatePassword);
      eventBus.off(AuthEventKeys.updatePassword.success, handleUpdatePasswordSuccess);

      eventBus.off(AuthEventKeys.activate.do, handleActivate);
      eventBus.off(AuthEventKeys.activate.success, handleActivateSuccess);
      eventBus.off(AuthEventKeys.activate.error, handleActivateError);

      eventBus.off(AuthEventKeys.completeRegistration.view, handleCompleteRegistrationNavigation);
      eventBus.off(AuthEventKeys.completeRegistration.do, handleCompleteRegistration);
    };
  }, [
    eventBus,
    handleLogin,
    handleRegister,
    handleForgotPassword,
    handleNewPassword,
    handleActivate,
    handleCompleteRegistration,
  ]);
  const baseProps = { dict, eventBus };

  const welcomeBack = !!checkoutAfterLoginObj;
  return (
    <>
      {formType === 'login' && !!auth && (
        <>
          <LoginForm
            eventBus={eventBus}
            dict={dict}
            auth={auth}
            loginProfileError={loginProfileError}
            prefilledEmail={prefilledEmail}
            welcomeBack={welcomeBack}
          />
          {!welcomeBack && <RegisterSection eventBus={eventBus} dict={dict} />}
        </>
      )}
      {formType === 'oidcLogin' && <OidcLoginForm eventBus={eventBus} dict={dict} />}
      {formType === 'register' && !!auth && <RegisterForm auth={auth} {...baseProps} />}
      {formType === 'completeRegistration' && !!auth && (
        <CompleteRegistrationForm auth={auth} {...baseProps} />
      )}
      {formType === 'activateResult' && <ActivateResult {...baseProps} />}
      {formType === 'RegisterEmailSent' && (
        <RegisterEmailSent
          {...baseProps}
          email={credentials?.email || ''}
          name={credentials?.name || ''}
        />
      )}
      {formType === 'forgotPassword' && <ForgotPasswordForm {...baseProps} />}
      {formType === 'forgotPasswordOk' && (
        <ForgotPasswordOk
          {...baseProps}
          email={credentials?.email || ''}
          setFormType={(value) => setFormType(value)}
        />
      )}
      {/* Aggiungi il rendering di NewPasswordOk */}
      {formType === 'newPasswordOk' && <NewPasswordOk {...baseProps} />}
      {formType === 'newPassword' && !!auth && <NewPassword auth={auth} {...baseProps} />}
      {/* Aggiungi il rendering di NewPasswordOk */}
      {formType === 'updatePasswordOk' && <UpdatePasswordOk {...baseProps} />}
      {formType === 'updatePassword' && !!auth && <UpdatePassword auth={auth} {...baseProps} />}
    </>
  );
}

export default LoginScreen;
